import { FC, useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import {
  FormFile,
  MainPage,
  PageTitle,
  PrimaryButton,
  HorizontalTab,
  LinkTextIcon,
  ModalLoading,
  ConfirmModalStatus,
} from '../../components'
import { useAppSelector } from '../../store/hooks'
import { exportFormatoShipmentInventoryXLSX } from '../../utils/exports'
import { read, utils, writeFile } from 'xlsx'
import InventoryImportReview, {
  InventoryImportOrder,
  InventoryImportProduct,
} from './InventoryImportReview'
import { alertService, SearchInventory } from '../../services'
import { CreateOrdern } from '../../services/InventoryServices'
import {
  CreateOrderRequest,
  ParcelItemPayload,
  ShipmentPayload,
  DeliveryOrderItem,
} from '../../interfaces/Inventory/CreateOrderRequest'
import { InventoryItemDto } from '../../interfaces/Inventory/InventoryItemDto'
import {
  BusinessType,
  BusinessUnitInterface,
  DeliveryEnum,
  PaymentModeId,
} from '../../interfaces'
import { ServiceDTO } from '../../interfaces/Services/Services'
import {
  getServicesFromPaymentMode,
  getServicesFromDeliveryType,
  getAppLockedServices,
} from '../../services/BusinessUnitServices'

let CONSIGNEE_TABS = [
  {
    name: 'Archivo',
    icon: <DocumentTextIcon className="w-5 h-5" />,
  },
]

const Inventory: FC = () => {
  const user = useAppSelector((state) => state.user)

  // Estado para controlar si la pestaña "Internacional" debe mostrarse
  const [showInternationalTab, setShowInternationalTab] = useState(false)

  // Efecto para actualizar el array de pestañas según la condición
  useEffect(() => {
    if (
      user?.client?.accountCode === '2500' ||
      user?.client?.accountCode === '2629'
    ) {
      setShowInternationalTab(true) // Mostrar la pestaña "Internacional"
    } else {
      setShowInternationalTab(false) // Ocultar la pestaña "Internacional"
    }
  }, [user])

  // Filtramos el array de pestañas según si debe mostrarse la pestaña "Internacional"
  const filteredTabs = CONSIGNEE_TABS.filter(
    (tab) => tab.name !== 'Internacional' || showInternationalTab
  )

  const businessUnits = useAppSelector((state) => state.inmutable.businessUnits)

  const businessUnitOptions = useMemo(() => {
    const business = businessUnits.filter(
      (businessUnit) =>
        //businessUnit.code !== '8110' &&
        businessUnit.buCode !== 'KK' &&
        businessUnit.buCode !== 'NN' &&
        //businessUnit.code !== 'MAD' &&
        //businessUnit.code !== 'MIA' &&
        //businessUnit.code !== 'PTY' &&
        !businessUnit.isLocked &&
        (businessUnit.buTypeID === BusinessType.EXTERNAL ||
          businessUnit.buTypeID === BusinessType.INTERNAL)
    )
    // Delete duplicates by id
    const uniqueIds = new Set()
    const result: BusinessUnitInterface[] = []
    business.forEach((businessUnit, index) => {
      if (!uniqueIds.has(businessUnit.buid)) {
        uniqueIds.add(businessUnit.buid)
        result.push(businessUnit)
      }
    })

    result.sort((a, b) => {
      return 0
    })

    return result
  }, [businessUnits, user.businessUnit])

  const identificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  )
  const citiesByBuType = useAppSelector((state) => state.inmutable.citiesByBu)

  const citiesType = useAppSelector((state) => state.inmutable.cities)
  const pieceCategories = useAppSelector((s) => s.inmutable.PieceCategories)
  const typeShipment = [
    { value: DeliveryEnum.OFFICE, name: 'Oficina' },
    { value: DeliveryEnum.DELIVERY, name: 'Domicilio' },
  ]

  const [tab, setTab] = useState(0)
  const [error, setError] = useState('')
  const [file, setFile] = useState<File>()
  const [nextSave, setNextSave] = useState(false)
  const [parsedRows, setParsedRows] = useState<InventoryImportOrder[]>([])
  const [reviewRows, setReviewRows] = useState<InventoryImportOrder[]>([])
  const [reviewHasErrors, setReviewHasErrors] = useState<boolean>(false)
  const [inventoryItems, setInventoryItems] = useState<InventoryItemDto[]>([])
  const [inventoryFetched, setInventoryFetched] = useState(false)
  const [initialValidationDone, setInitialValidationDone] = useState(false)
  const [initialValidationError, setInitialValidationError] = useState<
    string | null
  >(null)
  // ---- Servicio dinámico (allowedServices) ----
  const [paymentModeServices, setPaymentModeServices] = useState<ServiceDTO[]>(
    []
  )
  const [deliveryTypeServices, setDeliveryTypeServices] = useState<
    ServiceDTO[]
  >([])
  const [appBlockedServices, setAppBlockedServices] = useState<ServiceDTO[]>([])
  const [allowedServices, setAllowedServices] = useState<ServiceDTO[]>([])
  const applicationID = useAppSelector((s) => s.inmutable.appData.applicationID)

  let [loading, setLoading] = useState(false)
  let [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING
  )
  let [loadingTitle, setLoadingTitle] = useState('Cargando...')
  const [createdOrders, setCreatedOrders] = useState<any[]>([])
  const [showNoInventoryModal, setShowNoInventoryModal] = useState(false)
  const [showApiErrorModal, setShowApiErrorModal] = useState(false)
  const [apiErrorMessage, setApiErrorMessage] = useState('')

  const [step, setStep] = useState(0)

  const downloadRelacion = () => {
    try {
      const rows = (createdOrders || []).map((it: any) => ({
        Orden: it.shipment?.shipment_number ?? '',
        Guia: it?.picking_name ?? '',
        'Tienda Destino': `${it.shipment?.destination_store?.code ?? ''} - ${
          it.shipment?.destination_store?.commercial_name ?? ''
        }`.trim(),
        Destino: it?.shipment?.destination_address?.address ?? '',
        Destinatario: it?.shipment?.consignee?.full_name ?? '',
        Correo: it?.shipment?.consignee?.email ?? '',
        Telefono: it?.shipment?.consignee?.phone ?? '',
        'Total piezas': it?.shipment?.total_pieces ?? '',
      }))
      const wb = utils.book_new()
      const ws = utils.json_to_sheet(rows)
      utils.book_append_sheet(wb, ws, 'Relación')
      const now = new Date()
      const pad2 = (n: number) => n.toString().padStart(2, '0')
      const yyyy = now.getFullYear()
      const mm = pad2(now.getMonth() + 1)
      const dd = pad2(now.getDate())
      const hh = pad2(now.getHours())
      const mi = pad2(now.getMinutes())
      const ss = pad2(now.getSeconds())
      const fileName = `Relacion_ordenes_${yyyy}${mm}${dd}_${hh}${mi}${ss}.xlsx`
      writeFile(wb, fileName)
    } catch (e) {
      console.error('Error generando Excel', e)
      alertService.error('Error', 'No se pudo generar el archivo Excel')
    }
  }

  const buildPayload = (rows: InventoryImportOrder[]): CreateOrderRequest => {
    const abbrShipper = user?.client?.abbreviationName?.toString().trim()
    const idNumShipper = user?.client?.identificationNumber?.toString().trim()
    const originBu = user.client?.businessUnit
    const originLoc = originBu?.location
    const originStoreCode = originBu?.buCode || ''
    const originLocationCode = originLoc?.locationCode || originLoc?.code || ''
    const originAddressLineSource =
      user.client?.fiscalAddress || originLoc?.address || ''
    const originAddressLine =
      originAddressLineSource && originAddressLineSource.length > 0
        ? originAddressLineSource
        : 'N/A Tienda Virtual'
    const origCoords = originLoc?.coordinates
    const paymentMode =
      user.paymentMethod?.[0]?.paymentModeID ?? PaymentModeId.CREDIT

    const delivery_order_list: DeliveryOrderItem[] = rows.map((r) => {
      const isHome = r.deliveryTypeName === 'Domicilio'

      let destBu = businessUnits.find((b) => b.buCode === r.buCode)

      if (isHome && destBu == undefined) {
        var buCodeLog = businessUnits.filter(
          (x) =>
            x.buCode ===
            citiesByBuType.find((x) => x.locationCode === r.cityCode)?.buCode
        )![0]
        destBu = buCodeLog
        console.log(buCodeLog)
      }

      const destLoc = destBu?.location
      const destLocationCode = isHome
        ? r.cityCode || destLoc?.locationCode || destLoc?.code || ''
        : destLoc?.locationCode || destLoc?.code || ''
      const destStoreCode = r.buCode || destBu?.buCode || ''
      const destCoords = destLoc?.coordinates
      console.log(r)

      console.log(destBu)
      console.log(destLoc)

      const parcelItems: ParcelItemPayload[] = (r.products || []).map((p) => ({
        height: p.height === 0 ? 1 : p.height || 0,
        width: p.width === 0 ? 1 : p.width || 0,
        length: p.length === 0 ? 1 : p.length || 0,
        weight: p.weight === 0 ? 1 : p.weight || 0,
        content_category: (p.categoryId as any) || 0,
        content_description: p.productName || null,
        product_id: isFinite(Number(p.productCode)) ? Number(p.productCode) : 0,
        quantity: p.quantity || 0,
      }))

      const shipment: ShipmentPayload = {
        origin: {
          home_pickup: false,
          store: originStoreCode,
          location: originLocationCode,
          coordinates: origCoords
            ? {
                latitude: origCoords.lat,
                longitude: origCoords.lng,
              }
            : undefined,
          address: {
            name: originLoc?.name || '',
            line: originAddressLine,
            landmark: originLoc?.code || '',
            postal_code: originLoc?.postalCode || '',
          },
        },
        destination: {
          home_delivery: isHome,
          store: destStoreCode,
          location: destLocationCode,
          ...(destCoords
            ? {
                coordinates: {
                  latitude: destCoords.lat,
                  longitude: destCoords.lng,
                },
              }
            : {}),
          address: {
            name: null,
            line: r.address || destLoc?.address || '',
            landmark: null,
            postal_code: r.postalCode || null,
          },
        },
        shipper: {
          identification: `${abbrShipper}${idNumShipper}` || '',
          name: user.client?.accountFullName || '',
          phone:
            (user.client?.listAccountPhone?.[0]?.countryPhoneAccessCode || '') +
            ' ' +
            (user.client?.listAccountPhone?.[0]?.phoneNumber || ''),
          email: user.client?.listAccountEmail?.[0]?.email || '',
        },
        consignee: {
          identification: (() => {
            const typeMatch = (identificationTypes || []).find(
              (t: any) =>
                (t?.abbreviationName || '').toString().trim().toLowerCase() ===
                (r.idTypeAbbr || '').toString().trim().toLowerCase()
            )
            const abbr = typeMatch?.abbreviationName || r.idTypeAbbr || ''
            return `${abbr}${r.idNumber || ''}`
          })(),
          name: r.name || '',
          phone: '+58 ' + r.phone || '',
          email: r.email || '',
        },
        billing: {
          service: allowedServices[0]?.serviceID || 60,
          payment_mode: +paymentMode,
          account_code: user.client?.accountCode || '',
        },
        declared_value: {
          amount: (r.products || []).reduce(
            (acc, p) => acc + (p.declaredValue || 0),
            0
          ),
          currency: 'USD',
          invoice_number: '',
        },
        notes: r.notes || '',
        partner_shipment_id: r.orderNumber || '',
        pct_discount: 0,
        parcels: {
          dimension_unit: 'cm',
          weight_unit: 'kg',
          items: parcelItems,
        },
      }

      return {
        picking_type_id: 73,
        source_location_id: 8,
        destination_location_id: 153,
        shipment,
      }
    })

    return { delivery_order_list }
  }

  const processOrder = async (payload: CreateOrderRequest) => {
    const res = await CreateOrdern(payload)
    if (!res.didError && res.model) {
      // Mostrar modal de éxito con números de orden y opción de descargar relación
      const orders = Array.isArray(res.model) ? res.model : []
      setCreatedOrders(orders)
      const orderNumbers = orders
        .map((o: any) => o.shipment?.shipment_number)
        .filter(Boolean)
      const displayed = orderNumbers.slice(0, 10)
      const numbersText =
        displayed.join(', ') + (orderNumbers.length > 10 ? ', ...' : '')
      const title =
        'Se registraron sus ordenes de forma correcta' +
        (orderNumbers.length ? `\n${numbersText}` : '')
      setLoadingStatus(ConfirmModalStatus.SUCCESS)
      setLoadingTitle(title)
      setLoading(true)
      // Limpiar estado de edición/lista, regresar a inicio
      setNextSave(false)
      setParsedRows([])
      setReviewRows([])
      setFile(undefined)
    } else {
      // Intentar formatear errores específicos
      const rawMsg = res.errorMessage || ''
      let finalMsg = rawMsg || 'Error creando órdenes'
      try {
        // Si el backend envía JSON en errorMessage
        const parsed = JSON.parse(rawMsg)
        const errorsArr = Array.isArray(parsed) ? parsed : [parsed]
        const detailErr = errorsArr.find(
          (e: any) => e?.code === 'shipping.ShipmentDetail.3'
        )
        if (detailErr?.error_data?.min_declared_value) {
          finalMsg = `No se pudo generar la orden: el valor declarado debe ser mayor o igual a ${Number(
            detailErr.error_data.min_declared_value
          ).toFixed(2)}`
        }
      } catch (_) {
        // Si no es JSON o falla el parse, usar mensaje plano
        if (rawMsg.includes('total declared value cannot be less')) {
          const match = rawMsg.match(/\$(\d+(?:\.\d+)?)/)
          if (match) {
            finalMsg = `No se pudo generar la orden: el valor declarado debe ser mayor o igual a ${match[1]}`
          }
        }
      }
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(finalMsg)
    }
  }

  const handleProcess = async () => {
    const rows = reviewRows && reviewRows.length ? reviewRows : parsedRows
    if (!rows.length) return

    if (user.client?.businessUnit === undefined) {
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(
        'Comuniquese con el departamento de Soporte Técnico. No tiene tienda origen asignado.'
      )
      setLoading(true)
      return
    }
    try {
      setLoading(true)
      setLoadingStatus(ConfirmModalStatus.PROCESSING)
      setLoadingTitle('Procesando…')
      const payload = buildPayload(rows)
      await processOrder(payload)
    } catch (e: any) {
      console.error(e)
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(e?.message || 'Error preparando payload')
      // Mantener el modal abierto para que el usuario lo cierre manualmente
    }
  }

  useEffect(() => {
    const run = async () => {
      try {
        const abbr = user?.client?.abbreviationName?.toString().trim()
        const idNum = user?.client?.identificationNumber?.toString().trim()
        if (!abbr || !idNum) return
        const identification = `${abbr}${idNum}`
        const res = await SearchInventory(identification, null)
        if (res.didError) {
          console.warn('Inventory API error:', res.errorMessage)
          setApiErrorMessage(
            res.errorMessage || 'Error desconocido en la API de inventario'
          )
          setShowApiErrorModal(true)
        } else if (Array.isArray(res.model)) {
          setInventoryItems(
            res.model.filter(
              (item) =>
                item.Length > 0 &&
                item.Width > 0 &&
                item.Height > 0 &&
                item.DeclareValues > 0
            )
          )
        }
      } catch (e) {
        console.warn('Inventory API call failed:', e)
      } finally {
        setInventoryFetched(true)
      }
    }
    run()
    // Solo cuando haya usuario disponible
  }, [user])

  // Check for no inventory after fetch
  useEffect(() => {
    if (inventoryFetched && inventoryItems.length === 0) {
      setShowNoInventoryModal(true)
    }
  }, [inventoryFetched, inventoryItems.length])

  // Servicios por modo de pago (Crédito)
  useEffect(() => {
    const load = async () => {
      const res = await getServicesFromPaymentMode(PaymentModeId.CREDIT)
      if (!res.didError && Array.isArray(res.model)) {
        setPaymentModeServices(res.model)
      }
    }
    load()
  }, [])

  // Servicios por tipo de entrega (Oficina base)
  useEffect(() => {
    const load = async () => {
      const res = await getServicesFromDeliveryType(DeliveryEnum.OFFICE)
      if (!res.didError && Array.isArray(res.model)) {
        setDeliveryTypeServices(res.model)
      }
    }
    load()
  }, [])

  // Servicios bloqueados por aplicación
  useEffect(() => {
    if (applicationID == null) return
    const load = async () => {
      const res = await getAppLockedServices(applicationID)
      if (!res.didError && Array.isArray(res.model)) {
        setAppBlockedServices(res.model)
      }
    }
    load()
  }, [applicationID])

  // Calcular permitidos (intersección paymentMode + deliveryType - bloqueados)
  useEffect(() => {
    let base = paymentModeServices
    if (deliveryTypeServices.length > 0) {
      const deliverySet = new Set(deliveryTypeServices.map((s) => s.serviceID))
      base = base.filter((s) => deliverySet.has(s.serviceID))
    }
    if (appBlockedServices.length > 0) {
      const blocked = new Set(appBlockedServices.map((s) => s.serviceID))
      base = base.filter((s) => !blocked.has(s.serviceID))
    }
    setAllowedServices(base)
  }, [paymentModeServices, deliveryTypeServices, appBlockedServices])

  // Validación inicial (businessUnit y cuenta para facturar) similar a CreateShipmentV2
  useEffect(() => {
    if (!user || initialValidationDone) return
    const missingData: string[] = []
    if (!user.client?.businessUnit) {
      missingData.push('tienda origen asignada')
    }
    const paymentModeid =
      user.paymentMethod?.[0]?.paymentModeID ?? PaymentModeId.CREDIT
    const accountBillTo = user.paymentMethod?.filter(
      (x) => x.paymentModeID?.toString() === paymentModeid?.toString()
    )?.[0]?.accountBillTo

    console.log(accountBillTo)
    console.log(user.paymentMethod)

    if (
      (accountBillTo === '00000000-0000-0000-0000-000000000000' ||
        accountBillTo === undefined) &&
      paymentModeid.toString() === PaymentModeId.CREDIT.toString()
    ) {
      missingData.push('cuenta para facturar asignada')
    }
    if (missingData.length > 0) {
      setLoadingStatus(ConfirmModalStatus.ERROR)
      if (missingData.length === 2) {
        const msg =
          'No tiene cuenta para facturar asignada y no tiene tienda origen asignada. Por favor comuníquese con Soporte Técnico.'
        setLoadingTitle(msg)
        setInitialValidationError(msg)
      } else {
        const msg = `Comuníquese con el departamento de Soporte Técnico. No tiene ${missingData[0]}.`
        setLoadingTitle(msg)
        setInitialValidationError(msg)
      }
      setLoading(true)
    }
    setInitialValidationDone(true)
  }, [user, initialValidationDone])

  const handleFileChange = (file?: File) => {
    if (initialValidationError) {
      // Bloquear selección de archivo mientras existan errores iniciales
      setError(initialValidationError)
      return
    }
    if (!file) {
      setFile(undefined)
      setParsedRows([])
      return
    }
    const fileType = file.type
    if (
      fileType === 'text/csv' ||
      fileType === 'application/vnd.ms-excel' ||
      fileType ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setFile(file)
      setError('')
    } else {
      setError('El archivo no es csv, xls o xlsx')
    }
  }

  const expectedHeaders = [
    'Número de pedido',
    'Tipo de documento destinatario',
    'Número de documento destinatario',
    'Nombre destinatario',
    'Teléfono destinatario',
    'Correo destinatario',
    'Tipo de entrega (retiro por oficina/domicilio)',
    'Código de tienda destino (si es retiro por oficina)',
    'Ciudad/Localidad destino (si es domicilio)',
    'Dirección destino (si es domicilio)',
    'Código postal destino (opcional)',
    'Observaciones (opcional)',
    'Nombre producto',
    'Código producto',
    'Cantidad',
  ] as const

  type HeaderName = (typeof expectedHeaders)[number]

  const handleFileSubmit = async () => {
    if (!file) {
      setError('Debe seleccionar un archivo')
      return
    }
    try {
      setLoading(true)
      setLoadingStatus(ConfirmModalStatus.PROCESSING)
      setLoadingTitle('Leyendo archivo…')

      const buf = await file.arrayBuffer()
      const wb = read(buf, { type: 'array' })
      const sheetName = wb.SheetNames[0]
      const ws = wb.Sheets[sheetName]
      if (!ws) throw new Error('No se encontró hoja en el archivo')

      const rowsAoa: any[][] = utils.sheet_to_json(ws, {
        header: 1,
        defval: '',
      })
      if (rowsAoa.length <= 1) {
        throw new Error('El archivo está vacío o no tiene datos')
      }
      const headers = (rowsAoa[0] || []).map((h) =>
        (h || '').toString().trim()
      ) as string[]

      const missing = expectedHeaders.filter((h) => !headers.includes(h))
      if (missing.length > 0) {
        throw new Error(
          `Faltan columnas requeridas: ${missing.join(
            ', '
          )}. Use el formato descargado.`
        )
      }

      const idx = (name: HeaderName) => headers.indexOf(name)
      // Usar ID fijo de categoría "Otras Categorías" (49)
      const mappedFlat = rowsAoa.slice(1).map((r) => {
        const productCode = (r[idx('Código producto')] ?? '').toString().trim()
        const inv = inventoryItems.find((it) => it.Id === productCode)

        const flat = {
          orderNumber: (r[idx('Número de pedido')] ?? '').toString().trim(),
          idTypeAbbr: (r[idx('Tipo de documento destinatario')] ?? '')
            .toString()
            .trim(),
          idNumber: (r[idx('Número de documento destinatario')] ?? '')
            .toString()
            .trim(),
          name: (r[idx('Nombre destinatario')] ?? '').toString().trim(),
          phone: (r[idx('Teléfono destinatario')] ?? '').toString().trim(),
          email: (r[idx('Correo destinatario')] ?? '').toString().trim(),
          deliveryTypeName: (
            r[idx('Tipo de entrega (retiro por oficina/domicilio)')] ?? ''
          )
            .toString()
            .trim(),
          buCode:
            (
              r[idx('Código de tienda destino (si es retiro por oficina)')] ??
              ''
            )
              .toString()
              .trim()
              .split(' - ')[0] || '',
          cityCode:
            citiesType.find(
              (c) =>
                c.locationName ===
                (r[idx('Ciudad/Localidad destino (si es domicilio)')] ?? '')
                  .toString()
                  .trim()
            )?.locationCode || '',
          address: (r[idx('Dirección destino (si es domicilio)')] ?? '')
            .toString()
            .trim(),
          postalCode: (r[idx('Código postal destino (opcional)')] ?? '')
            .toString()
            .trim(),
          notes: (r[idx('Observaciones (opcional)')] ?? '').toString().trim(),
          product: {
            productCode,
            productName:
              inv?.Name ?? (r[idx('Nombre producto')] ?? '').toString().trim(),
            quantity: Number(r[idx('Cantidad')] ?? 0) || 0,
            width: inv?.Width,
            height: inv?.Height,
            length: inv?.Length,
            weight: inv?.Weight,
            declaredValue: inv?.DeclareValues,
            categoryId: 49,
          } as InventoryImportProduct,
        }
        return flat
      })
      // Remove blank rows (no orderNumber and no productCode)
      const filtered = mappedFlat.filter(
        (m) => m.orderNumber || m.product.productCode
      )
      if (filtered.length === 0)
        throw new Error('No hay filas válidas para importar')

      // Group by orderNumber; take first row's consignee fields; accumulate products
      const ordersMap = new Map<string, InventoryImportOrder>()
      for (const f of filtered) {
        const key = f.orderNumber || ''
        if (!ordersMap.has(key)) {
          ordersMap.set(key, {
            orderNumber: f.orderNumber,
            idTypeAbbr: f.idTypeAbbr,
            idNumber: f.idNumber,
            name: f.name,
            phone: f.phone,
            email: f.email,
            deliveryTypeName: f.deliveryTypeName,
            buCode: f.buCode,
            cityCode: f.cityCode,
            address: f.address,
            postalCode: f.postalCode,
            notes: f.notes,
            products: [],
          })
        }
        const order = ordersMap.get(key)!
        order.products.push({ ...f.product })
      }

      const grouped = Array.from(ordersMap.values())
      setParsedRows(grouped)
      setNextSave(true)
    } catch (err: any) {
      console.error(err)
      setError(err?.message || 'Error leyendo el archivo')
    } finally {
      setLoading(false)
    }
  }

  return (
    <MainPage>
      <PageTitle title="Ordenes → Despacho" />

      {!nextSave && (
        <>
          {step === 1 && (
            <HorizontalTab
              current={tab}
              tabs={filteredTabs}
              onChange={setTab}
            />
          )}
          <div className="flex flex-1 flex-col items-center justify-center mt-6 gap-4">
            {step === 0 ? (
              <>
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">
                    Formato de Importacion
                  </h1>
                  <p className="text-sm text-gray-500 mb-6">
                    Este formato se actualiza en cada descarga.
                  </p>
                </div>
                <div className="flex gap-4">
                  <PrimaryButton
                    onClick={async () => {
                      const filteredItems = inventoryItems.filter(
                        (item) =>
                          item.Length > 0 &&
                          item.Width > 0 &&
                          item.Height > 0 &&
                          item.DeclareValues > 0
                      )
                      await exportFormatoShipmentInventoryXLSX(
                        identificationTypes,
                        typeShipment,
                        businessUnitOptions,
                        citiesType,
                        filteredItems
                      )
                      setStep(1)
                    }}
                    disabled={!inventoryFetched || inventoryItems.length === 0}
                  >
                    Descargar
                    <DocumentArrowDownIcon className="w-5 h-5" />
                  </PrimaryButton>
                </div>
              </>
            ) : (
              tab === 0 && (
                <>
                  {!inventoryFetched ? null : (
                    <>
                      <div className="w-full flex justify-end items-end">
                        <PrimaryButton
                          onClick={async () => {
                            const filteredItems = inventoryItems.filter(
                              (item) =>
                                item.Length > 0 &&
                                item.Width > 0 &&
                                item.Height > 0 &&
                                item.DeclareValues > 0
                            )
                            await exportFormatoShipmentInventoryXLSX(
                              identificationTypes,
                              typeShipment,
                              businessUnitOptions,
                              citiesType,
                              filteredItems
                            )
                          }}
                          className="w-full sm:w-40"
                          disabled={inventoryItems.length === 0}
                        >
                          Descargar Formato
                          <DocumentArrowDownIcon className="w-5 h-5"></DocumentArrowDownIcon>
                        </PrimaryButton>
                      </div>
                      <div className="w-full" style={{ maxWidth: '35rem' }}>
                        <FormFile
                          selected={file}
                          error={error}
                          label="Por favor seleccione el archivo que contiene los envíos a importar."
                          description="Archivos admitidos: CSV | XLS | XLSX"
                          id="shipment-import-file"
                          name="shipment-import-file"
                          onSelectFile={handleFileChange}
                        />
                      </div>
                      <PrimaryButton
                        onClick={handleFileSubmit}
                        className="w-full sm:w-32"
                        disabled={
                          !file ||
                          !!initialValidationError ||
                          inventoryItems.length === 0
                        }
                      >
                        Cargar
                      </PrimaryButton>
                    </>
                  )}
                </>
              )
            )}
          </div>
        </>
      )}

      {nextSave && (
        <>
          <div
            className="flex flex-1 justify-between gap-12 md:gap-12 flex-col-reverse md:flex-row mt-2 "
            style={{ maxWidth: '100rem' }}
          >
            <LinkTextIcon
              className="text-sm font-medium cursor-pointer"
              onClick={() => {
                setNextSave(false)
                setFile(undefined)
                setParsedRows([])
                setError('')
              }}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" aria-hidden="true" />{' '}
              <label>Regresar</label>
            </LinkTextIcon>
            <PrimaryButton
              className="sm:w-32 h-9 self-end md:self-auto"
              disabled={reviewHasErrors || (reviewRows?.length || 0) === 0}
              onClick={handleProcess}
            >
              <span className="flex items-center gap-2">
                <span>Procesar</span>
                <Icon
                  icon="mdi:content-save"
                  className="w-4 h-4"
                  aria-hidden="true"
                />
              </span>
            </PrimaryButton>
          </div>
          <div className="mt-4">
            <InventoryImportReview
              rows={parsedRows}
              onClose={() => setNextSave(false)}
              identificationTypes={identificationTypes}
              typeShipment={typeShipment}
              businessUnits={businessUnits}
              cities={citiesType}
              pieceCategories={pieceCategories as any}
              onRowsChange={(rows) => setReviewRows(rows)}
              onHasErrorsChange={(v) => setReviewHasErrors(v)}
              inventoryItems={inventoryItems}
            />
          </div>
        </>
      )}

      <ModalLoading
        open={!inventoryFetched}
        title="Cargando..."
        status={ConfirmModalStatus.PROCESSING}
        setOpen={() => {}}
        onPrint={() => {}}
        timeOut={120000}
      />

      <ModalLoading
        open={showNoInventoryModal}
        title="No existen Inventarios disponible, Agregue o contacte a soporte tecnico"
        status={ConfirmModalStatus.ERROR}
        setOpen={setShowNoInventoryModal}
        onPrint={() => {}}
        timeOut={120000}
      />

      <ModalLoading
        open={showApiErrorModal}
        title={apiErrorMessage}
        status={ConfirmModalStatus.ERROR}
        setOpen={setShowApiErrorModal}
        onPrint={() => {}}
        timeOut={120000}
      />

      <ModalLoading
        open={loading}
        title={loadingTitle}
        status={loadingStatus}
        setOpen={setLoading}
        onPrint={() => {}}
        timeOut={120000}
        actionLabel={
          loadingStatus === ConfirmModalStatus.SUCCESS &&
          (createdOrders?.length || 0) > 0
            ? 'Descargar relación'
            : undefined
        }
        onAction={
          loadingStatus === ConfirmModalStatus.SUCCESS &&
          (createdOrders?.length || 0) > 0
            ? downloadRelacion
            : undefined
        }
      />
    </MainPage>
  )
}

export default Inventory
